import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Stack, Typography,
} from "@mui/material";
import { EVENT_LINK_LIST, STATUS_COLOR } from "../constants";
import { CONTRACT_STATUS, fmtDateTime } from "../contracts";
import { useFullScreenDialog } from "../useFullScreenDialog";
import LinkList, { LINKS_API } from "./LinkList";
import { RichTextView } from "./RichText";

// A label/value row for the personal details block.
function Detail({ label, children }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>{label}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}

const fmtFee = (fee) =>
  fee == null ? null : `£${Number(fee).toFixed(2)}`;

/**
 * What one person sees for one of their events: the event's general information
 * (free text + document/external links, maintained by an admin on the Event) and
 * then the parts specific to them, taken from their own attendance record.
 *
 * Props: attendance (the row, with its `event`; null when closed), dateRange, onClose,
 * contracts (their engagement schedules for this attendance), onOpenContract(contract).
 */
export default function MyEventDialog({ attendance, dateRange, onClose, contracts = [], onOpenContract }) {
  const fullScreen = useFullScreenDialog();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const eventId = attendance?.event_id;

  useEffect(() => {
    if (!eventId) { setLinks([]); return; }
    setLoading(true);
    axios.get(LINKS_API + "/", { params: { kind: EVENT_LINK_LIST.kind, event_id: eventId } })
      .then(res => setLinks(res.data || []))
      .catch(() => setLinks([]))
      .finally(() => setLoading(false));
  }, [eventId]);

  const event = attendance?.event;
  const fee = fmtFee(attendance?.fee);

  return (
    <Dialog open={!!attendance} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>
        {event?.label_long || event?.name || "Event"}
        <Typography variant="body2" color="text.secondary">{dateRange(event || {})}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Divider textAlign="left">Event information</Divider>
          {event?.information ? (
            <RichTextView value={event.information} dense />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No general information has been added for this event.
            </Typography>
          )}
          {loading
            ? <Stack sx={{ alignItems: "center", py: 2 }}><CircularProgress size={24} /></Stack>
            : <LinkList links={links} empty={EVENT_LINK_LIST.empty} />}

          <Divider textAlign="left">Your details</Divider>
          <Detail label="Event role">
            <Typography variant="body2">{attendance?.role || "No event role set"}</Typography>
          </Detail>
          <Detail label="Status">
            {attendance?.status
              ? <Chip size="small" label={attendance.status}
                      color={STATUS_COLOR[attendance.status] || "default"} />
              : <Typography variant="body2" color="text.secondary">Not set</Typography>}
          </Detail>
          <Detail label="Fee">
            <Typography variant="body2" color={fee ? "text.primary" : "text.secondary"}>
              {fee || "Not set"}
            </Typography>
          </Detail>
          <Detail label="Shift details">
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}
                        color={attendance?.shift_details ? "text.primary" : "text.secondary"}>
              {attendance?.shift_details || "Not set"}
            </Typography>
          </Detail>

          {contracts.length > 0 && (
            <>
              <Divider textAlign="left">Your contract</Divider>
              {contracts.map(c => {
                const status = CONTRACT_STATUS[c.status] || { label: c.status, color: "default" };
                return (
                  <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
                      {c.documents[0]?.title || "Engagement schedule"}
                      <Typography component="span" variant="caption" color="text.secondary">
                        {" "}— {c.status === "completed" ? `signed ${fmtDateTime(c.completed_at)}` : `sent ${fmtDateTime(c.sent_at)}`}
                      </Typography>
                    </Typography>
                    <Chip size="small" label={c.status === "sent" ? "Waiting for you" : status.label}
                          color={status.color} />
                    <Button size="small" variant={c.status === "sent" ? "contained" : "text"}
                            onClick={() => onOpenContract?.(c)}>
                      {c.status === "sent" ? "Review and sign" : "View"}
                    </Button>
                  </Box>
                );
              })}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
